import { useState, useEffect, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AuthContext, apiClient } from '../App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Label } from "@/components/ui/label";
import { 
  Search, 
  Users, 
  Calendar, 
  Shield, 
  XCircle, 
  CheckCircle,
  Ban,
  TestTube,
  ChevronLeft,
  RefreshCw,
  Clock,
  FileText,
  BarChart3,
  Bell,
  CalendarDays,
  Building2
} from 'lucide-react';
import AdminHeader from '../components/AdminHeader';
import { formatLocalDisplay, getUserTimezone } from '../utils/datetime';

// Helper: Calculate age from date of birth (YYYY-MM-DD)
const calculateAgeFromDob = (dob) => {
  if (!dob) return null;
  try {
    const [year, month, day] = dob.split('-').map(Number);
    const birthDate = new Date(year, month - 1, day);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  } catch {
    return null;
  }
};

export const AdminUserProfileLink = ({ username, navigate, prefix = '/player', children, ...props }) => {
  const available = typeof username === 'string' && username.trim().length > 0;
  return (
    <button
      {...props}
      disabled={!available}
      onClick={() => { if (available) navigate(`${prefix}/${encodeURIComponent(username)}`); }}
    >
      {children || (available ? username : 'Player (profile unavailable)')}
    </button>
  );
};

export const isAdminGamePrivate = (game) => (
  game?.is_private === true || game?.privacy === 'private'
);

export const formatAdminGameDate = (dateTime, timezone) => {
  if (!dateTime) return 'Date unavailable';

  try {
    const formatted = formatLocalDisplay(dateTime, timezone || getUserTimezone());
    return formatted && formatted !== 'Invalid Date' ? formatted : 'Date unavailable';
  } catch {
    return 'Date unavailable';
  }
};

export const AdminGameRow = ({ game, onCancel, onToggleTest }) => {
  const isPrivate = isAdminGamePrivate(game);

  return (
    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <Link
            to={`/games/${game.id}`}
            className="font-medium truncate text-zinc-900 hover:text-orange-700 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500"
            data-testid={`admin-game-link-${game.id}`}
          >
            {game.title}
          </Link>
          <Badge
            variant="outline"
            className="text-xs"
            data-testid={`admin-game-privacy-${game.id}`}
          >
            {isPrivate ? 'Private' : 'Public'}
          </Badge>
          {game.status === 'cancelled' && <Badge variant="destructive" className="text-xs">Cancelled</Badge>}
          {game.is_test_game && <Badge variant="secondary" className="text-xs">Test</Badge>}
          {game.cancelled_by_admin && <Badge className="text-xs bg-purple-500">Admin Cancel</Badge>}
        </div>
        <div className="text-sm text-gray-500">
          Host: {game.creator_username} | {formatAdminGameDate(game.date_time, game.tz)}
        </div>
      </div>
      <div className="flex gap-1 ml-2">
        {game.status !== 'cancelled' && (
          <Button
            size="sm"
            variant="outline"
            className="text-red-600"
            onClick={() => onCancel(game.id, game.title)}
            aria-label={`Cancel ${game.title}`}
          >
            <XCircle className="w-4 h-4" />
          </Button>
        )}
        <Button
          size="sm"
          variant="outline"
          className={game.is_test_game ? 'text-yellow-600' : 'text-gray-400'}
          onClick={() => onToggleTest(game.id, game.title, game.is_test_game)}
          aria-label={`${game.is_test_game ? 'Unmark' : 'Mark'} ${game.title} as test`}
        >
          <TestTube className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};

const AdminPanel = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  
  // Stats
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  
  // Users
  const [userSearch, setUserSearch] = useState('');
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [userFilter, setUserFilter] = useState('all');
  const [usersTotal, setUsersTotal] = useState(0);
  const [usersLoadedAll, setUsersLoadedAll] = useState(false);
  
  // Disabled Users (dedicated list)
  const [disabledUsers, setDisabledUsers] = useState([]);
  const [disabledUsersLoading, setDisabledUsersLoading] = useState(false);
  
  // Games
  const [games, setGames] = useState([]);
  const [gamesLoading, setGamesLoading] = useState(false);
  const [gameFilter, setGameFilter] = useState('upcoming');
  
  // Action Logs
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsDateFrom, setLogsDateFrom] = useState('');
  const [logsDateTo, setLogsDateTo] = useState('');
  
  // Dialogs
  const [confirmDialog, setConfirmDialog] = useState({ open: false, action: null, target: null, reason: '' });

  // Active tab (controlled) so list data lazy-loads when a tab becomes active
  const [activeTab, setActiveTab] = useState('users');

  // Check admin access
  useEffect(() => {
    if (!user || !['admin', 'scout'].includes(user.role)) {
      navigate('/dashboard');
      toast.error('Admin access required');
    }
  }, [user, navigate]);

  // Load top-card stats on mount (always visible regardless of active tab)
  useEffect(() => {
    fetchStats();
  }, []);

  // Single owner of list fetching: load the active tab's list on activation,
  // and refetch when that tab's filter changes. Fixes the bug where the
  // default-selected "Upcoming" games filter looked active but never fetched.
  useEffect(() => {
    if (activeTab === 'games') {
      fetchGames(gameFilter);
    } else if (activeTab === 'users') {
      fetchUsers(userFilter);
    } else if (activeTab === 'disabled') {
      fetchDisabledUsers();
    }
  }, [activeTab, gameFilter, userFilter]);

  const fetchStats = async () => {
    setStatsLoading(true);
    try {
      const response = await apiClient.get('/admin/stats');
      setStats(response.data);
    } catch (error) {
      toast.error('Failed to load stats');
    } finally {
      setStatsLoading(false);
    }
  };

  const searchUsers = async () => {
    if (!userSearch || userSearch.length < 2) {
      toast.error('Enter at least 2 characters to search');
      return;
    }
    setUsersLoading(true);
    setUsersTotal(0);
    setUsersLoadedAll(true); // search doesn't paginate
    try {
      const response = await apiClient.get('/admin/users/search', {
        params: { q: userSearch, include_disabled: true }
      });
      setUsers(response.data.users);
    } catch (error) {
      const narrowerSearch = 'Search is too broad; narrow your username search.';
      if (error?.response?.status === 400 && error?.response?.data?.detail === narrowerSearch) {
        setUsers([]);
        setUsersTotal(0);
        toast.error(narrowerSearch);
      } else {
        toast.error('Search failed');
      }
    } finally {
      setUsersLoading(false);
    }
  };

  const fetchUsers = async (filter, append = false) => {
    setUsersLoading(true);
    try {
      const skip = append ? users.length : 0;
      const response = await apiClient.get('/admin/users/list', {
        params: { filter_type: filter, limit: 50, skip }
      });
      const newUsers = response.data.users;
      const total = response.data.total;
      
      if (append) {
        setUsers(prev => [...prev, ...newUsers]);
      } else {
        setUsers(newUsers);
      }
      setUsersTotal(total);
      setUsersLoadedAll((append ? users.length + newUsers.length : newUsers.length) >= total);
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setUsersLoading(false);
    }
  };

  const fetchDisabledUsers = async () => {
    setDisabledUsersLoading(true);
    try {
      const response = await apiClient.get('/admin/users/list', {
        params: { filter_type: 'disabled', limit: 100 }
      });
      setDisabledUsers(response.data.users);
    } catch (error) {
      toast.error('Failed to load disabled users');
    } finally {
      setDisabledUsersLoading(false);
    }
  };

  const fetchGames = async (filter) => {
    setGamesLoading(true);
    try {
      const response = await apiClient.get('/admin/games/list', {
        params: { filter_type: filter, limit: 50 }
      });
      setGames(response.data.games);
    } catch (error) {
      toast.error('Failed to load games');
    } finally {
      setGamesLoading(false);
    }
  };

  const fetchLogs = async () => {
    setLogsLoading(true);
    try {
      const params = { limit: 100 };
      if (logsDateFrom) params.date_from = logsDateFrom;
      if (logsDateTo) params.date_to = logsDateTo;
      
      const response = await apiClient.get('/admin/action-logs', { params });
      setLogs(response.data.logs);
    } catch (error) {
      toast.error('Failed to load action logs');
    } finally {
      setLogsLoading(false);
    }
  };

  // Action handlers
  const handleDisableUser = async (userId, username) => {
    setConfirmDialog({
      open: true,
      action: 'disable_user',
      target: { id: userId, name: username },
      reason: ''
    });
  };

  const handleEnableUser = async (userId, username) => {
    setConfirmDialog({
      open: true,
      action: 'enable_user',
      target: { id: userId, name: username },
      reason: ''
    });
  };

  const handleMarkTestUser = async (userId, username, currentTestStatus) => {
    try {
      await apiClient.post(`/admin/users/${userId}/mark-test`, null, {
        params: { is_test: !currentTestStatus }
      });
      toast.success(currentTestStatus ? 'User unmarked as test' : 'User marked as test');
      fetchUsers(userFilter);
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    }
  };

  const handleCancelAllUserGames = async (userId, username) => {
    setConfirmDialog({
      open: true,
      action: 'cancel_all_games',
      target: { id: userId, name: username },
      reason: ''
    });
  };

  const handleCancelGame = async (gameId, gameTitle) => {
    setConfirmDialog({
      open: true,
      action: 'cancel_game',
      target: { id: gameId, name: gameTitle },
      reason: ''
    });
  };

  const handleMarkTestGame = async (gameId, gameTitle, currentTestStatus) => {
    try {
      await apiClient.post(`/admin/games/${gameId}/mark-test`, null, {
        params: { is_test: !currentTestStatus }
      });
      toast.success(currentTestStatus ? 'Game unmarked as test' : 'Game marked as test');
      fetchGames(gameFilter);
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update game');
    }
  };

  const executeAction = async () => {
    const { action, target, reason } = confirmDialog;
    try {
      switch (action) {
        case 'disable_user':
          await apiClient.post(`/admin/users/${target.id}/disable`, null, {
            params: { reason: reason || undefined }
          });
          toast.success(`User ${target.name} disabled`);
          break;
        case 'enable_user':
          await apiClient.post(`/admin/users/${target.id}/enable`, null, {
            params: { reason: reason || undefined }
          });
          toast.success(`User ${target.name} enabled`);
          break;
        case 'cancel_all_games':
          const result = await apiClient.post(`/admin/users/${target.id}/cancel-all-games`, null, {
            params: { reason: reason || undefined }
          });
          toast.success(`Cancelled ${result.data.games_cancelled} games for ${target.name}`);
          break;
        case 'cancel_game':
          await apiClient.post(`/admin/games/${target.id}/cancel`, null, {
            params: { reason: reason || undefined }
          });
          toast.success(`Game "${target.name}" cancelled`);
          break;
        default:
          break;
      }
      setConfirmDialog({ open: false, action: null, target: null, reason: '' });
      fetchStats();
      if (action.includes('user')) fetchUsers(userFilter);
      if (action.includes('game')) fetchGames(gameFilter);
      fetchLogs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Action failed');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      // Ensure the date string is treated as UTC if no timezone info
      const date = new Date(dateStr.endsWith('Z') ? dateStr : dateStr + 'Z');
      return date.toLocaleString(undefined, {
        month: 'numeric',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit'
      });
    } catch {
      return 'N/A';
    }
  };

  const getActionLabel = (action) => {
    const labels = {
      disable_user: 'Disabled User',
      enable_user: 'Enabled User',
      cancel_game: 'Cancelled Game',
      cancel_all_user_games: 'Cancelled All Games',
      mark_test_user: 'Marked Test User',
      unmark_test_user: 'Unmarked Test User',
      mark_test_game: 'Marked Test Game',
      unmark_test_game: 'Unmarked Test Game'
    };
    return labels[action] || action;
  };

  if (!user || !['admin', 'scout'].includes(user.role)) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Shared AdminHeader */}
      <AdminHeader title="Admin Panel" onRefresh={fetchStats} />

      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Users</p>
                  <p className="text-2xl font-bold">{stats?.users?.total || 0}</p>
                </div>
                <Users className="w-8 h-8 text-blue-500 opacity-50" />
              </div>
              <div className="mt-2 text-xs text-gray-500">
                <span className="text-red-500">{stats?.users?.disabled || 0} disabled</span>
                {' | '}
                <span className="text-yellow-600">{stats?.users?.test || 0} test</span>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Games</p>
                  <p className="text-2xl font-bold">{stats?.games?.total || 0}</p>
                </div>
                <Calendar className="w-8 h-8 text-green-500 opacity-50" />
              </div>
              <div className="mt-2 text-xs text-gray-500">
                <span className="text-red-500">{stats?.games?.cancelled || 0} cancelled</span>
                {' | '}
                <span className="text-yellow-600">{stats?.games?.test || 0} test</span>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Upcoming Games</p>
                  <p className="text-2xl font-bold">{stats?.games?.upcoming || 0}</p>
                </div>
                <Clock className="w-8 h-8 text-orange-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Admin Actions (7d)</p>
                  <p className="text-2xl font-bold">{stats?.admin_actions_last_7_days || 0}</p>
                </div>
                <FileText className="w-8 h-8 text-purple-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="users">Users</TabsTrigger>
            <TabsTrigger value="disabled">Disabled ({stats?.users?.disabled || 0})</TabsTrigger>
            <TabsTrigger value="games">Games</TabsTrigger>
            <TabsTrigger value="logs">Action Logs</TabsTrigger>
          </TabsList>

          {/* Users Tab */}
          <TabsContent value="users">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  User Management
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Search */}
                <div className="flex gap-2">
                  <Input
                    placeholder={user.role === 'admin' ? 'Search by username, email, or phone...' : 'Search by username...'}
                    value={userSearch}
                    onChange={(e) => setUserSearch(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && searchUsers()}
                    className="flex-1"
                  />
                  <Button onClick={searchUsers} disabled={usersLoading}>
                    <Search className="w-4 h-4 mr-2" />
                    Search
                  </Button>
                </div>

                {/* Filter Buttons */}
                <div className="flex gap-2 flex-wrap items-center">
                  {['all', 'active', 'disabled', 'test'].map((filter) => (
                    <Button
                      key={filter}
                      variant={userFilter === filter ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => {
                        setUserFilter(filter);
                        setUserSearch('');
                      }}
                      data-testid={`user-filter-${filter}`}
                    >
                      {filter.charAt(0).toUpperCase() + filter.slice(1)}
                    </Button>
                  ))}
                  {users.length === 0 && !usersLoading && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="border-orange-300 text-orange-600 hover:bg-orange-50"
                      onClick={() => {
                        setUserSearch('');
                        fetchUsers(userFilter);
                      }}
                      data-testid="show-all-users-btn"
                    >
                      <Users className="w-3.5 h-3.5 mr-1.5" />
                      Show All Users
                    </Button>
                  )}
                </div>

                {/* Count indicator */}
                {users.length > 0 && (
                  <p className="text-xs text-gray-500" data-testid="users-count">
                    Showing {users.length}{usersTotal > 0 ? ` of ${usersTotal}` : ''} users
                  </p>
                )}

                {/* Users List */}
                <div className="space-y-2 max-h-[600px] overflow-y-auto">
                  {usersLoading && users.length === 0 ? (
                    <p className="text-center text-gray-500 py-4">Loading...</p>
                  ) : users.length === 0 ? (
                    <p className="text-center text-gray-500 py-4">No users found. Use search or click a filter above.</p>
                  ) : (
                    <>
                      {users.map((u) => (
                        <div 
                          key={u.id} 
                          className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border"
                        >
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <AdminUserProfileLink
                                username={u.username}
                                navigate={navigate}
                                className="font-medium truncate text-blue-600 hover:text-blue-800 hover:underline"
                                data-testid={`admin-user-link-${u.username}`}
                              >
                              </AdminUserProfileLink>
                              {u.is_disabled && <Badge variant="destructive" className="text-xs">Disabled</Badge>}
                              {u.is_test_user && <Badge variant="secondary" className="text-xs">Test</Badge>}
                              {u.role === 'host' && <Badge className="text-xs bg-orange-500">Host</Badge>}
                              {u.role === 'admin' && <Badge className="text-xs bg-purple-500">Admin</Badge>}
                            </div>
                            <div className="text-sm text-gray-500 truncate">
                              {u.email} {u.phone_number && `| ${u.phone_number}`}
                              {u.date_of_birth && ` | Age: ${calculateAgeFromDob(u.date_of_birth)}`}
                            </div>
                          </div>
                          <div className="flex gap-1 ml-2">
                            {u.is_disabled ? (
                              <Button 
                                size="sm" 
                                variant="outline" 
                                className="text-green-600"
                                onClick={() => handleEnableUser(u.id, u.username)}
                              >
                                <CheckCircle className="w-4 h-4" />
                              </Button>
                            ) : (
                              <Button 
                                size="sm" 
                                variant="outline" 
                                className="text-red-600"
                                onClick={() => handleDisableUser(u.id, u.username)}
                                disabled={u.id === user.id}
                              >
                                <Ban className="w-4 h-4" />
                              </Button>
                            )}
                            <Button 
                              size="sm" 
                              variant="outline"
                              className={u.is_test_user ? 'text-yellow-600' : 'text-gray-400'}
                              onClick={() => handleMarkTestUser(u.id, u.username, u.is_test_user)}
                            >
                              <TestTube className="w-4 h-4" />
                            </Button>
                            {u.role === 'host' && (
                              <Button 
                                size="sm" 
                                variant="outline" 
                                className="text-orange-600"
                                onClick={() => handleCancelAllUserGames(u.id, u.username)}
                              >
                                <XCircle className="w-4 h-4" />
                              </Button>
                            )}
                            <Button 
                              size="sm" 
                              variant="outline" 
                              className="text-blue-600"
                              onClick={() => navigate(`/admin/users/${u.id}/controls`)}
                              data-testid={`admin-manage-${u.username}`}
                            >
                              <Shield className="w-4 h-4" />
                            </Button>
                            {user.role === 'admin' && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-purple-600"
                                onClick={() => navigate(`/admin/hosts/${u.id}`)}
                                data-testid={`admin-host-view-${u.username}`}
                                title="View host data (read-only)"
                              >
                                <Building2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                      {/* Load More */}
                      {!usersLoadedAll && users.length > 0 && (
                        <div className="pt-2 text-center">
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={usersLoading}
                            onClick={() => fetchUsers(userFilter, true)}
                            data-testid="load-more-users-btn"
                          >
                            {usersLoading ? 'Loading...' : `Load More (${usersTotal - users.length} remaining)`}
                          </Button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Disabled Users Tab */}
          <TabsContent value="disabled">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Ban className="w-5 h-5 text-red-500" />
                  Disabled Users
                </CardTitle>
                <Button variant="outline" size="sm" onClick={fetchDisabledUsers}>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Refresh
                </Button>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 max-h-[500px] overflow-y-auto">
                  {disabledUsersLoading ? (
                    <p className="text-center text-gray-500 py-4">Loading...</p>
                  ) : disabledUsers.length === 0 ? (
                    <p className="text-center text-gray-500 py-4">No disabled users.</p>
                  ) : (
                    disabledUsers.map((u) => (
                      <div 
                        key={u.id} 
                        className="flex items-center justify-between p-4 bg-red-50 rounded-lg border border-red-200"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium truncate">{u.username || 'Player (profile unavailable)'}</span>
                            <Badge variant="destructive" className="text-xs">Disabled</Badge>
                            {u.role === 'host' && <Badge className="text-xs bg-orange-500">Host</Badge>}
                          </div>
                          <div className="text-sm text-gray-600 truncate">
                            {u.email} {u.phone_number && `| ${u.phone_number}`}
                          </div>
                          {u.disabled_at && (
                            <div className="text-xs text-gray-500 mt-1">
                              Disabled: {formatDate(u.disabled_at)}
                              {u.disabled_reason && <span> - "{u.disabled_reason}"</span>}
                            </div>
                          )}
                        </div>
                        <div className="flex gap-2 ml-2">
                          <Button 
                            size="sm" 
                            className="bg-orange-500 hover:bg-orange-600 text-white"
                            onClick={() => handleEnableUser(u.id, u.username)}
                          >
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Enable
                          </Button>
                          <AdminUserProfileLink
                            username={u.username}
                            navigate={navigate}
                            prefix="/user"
                            className="px-3 py-2 border rounded-md disabled:opacity-50"
                          >
                            View Profile
                          </AdminUserProfileLink>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Games Tab */}
          <TabsContent value="games">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="w-5 h-5" />
                  Game Management
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Filter Buttons */}
                <div className="flex gap-2 flex-wrap">
                  {['upcoming', 'all', 'cancelled', 'test'].map((filter) => (
                    <Button
                      key={filter}
                      variant={gameFilter === filter ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setGameFilter(filter)}
                      data-testid={`game-filter-${filter}`}
                    >
                      {filter.charAt(0).toUpperCase() + filter.slice(1)}
                    </Button>
                  ))}
                </div>

                {/* Games List */}
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {gamesLoading ? (
                    <p className="text-center text-gray-500 py-4">Loading...</p>
                  ) : games.length === 0 ? (
                    <p className="text-center text-gray-500 py-4" data-testid="admin-games-empty">No games match this filter.</p>
                  ) : (
                    games.map((g) => (
                      <AdminGameRow
                        key={g.id}
                        game={g}
                        onCancel={handleCancelGame}
                        onToggleTest={handleMarkTestGame}
                      />
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Action Logs Tab */}
          <TabsContent value="logs">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Admin Action Logs
                </CardTitle>
                <Button variant="outline" size="sm" onClick={fetchLogs}>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Load Logs
                </Button>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Date Range Filter */}
                <div className="flex gap-4 items-end flex-wrap bg-gray-50 p-3 rounded-lg">
                  <div className="flex items-center gap-2">
                    <CalendarDays className="w-4 h-4 text-gray-500" />
                    <span className="text-sm font-medium text-gray-700">Date Range:</span>
                  </div>
                  <div className="flex gap-2 items-center">
                    <Input
                      type="date"
                      value={logsDateFrom}
                      onChange={(e) => setLogsDateFrom(e.target.value)}
                      className="w-40"
                    />
                    <span className="text-gray-500">to</span>
                    <Input
                      type="date"
                      value={logsDateTo}
                      onChange={(e) => setLogsDateTo(e.target.value)}
                      className="w-40"
                    />
                  </div>
                  <Button size="sm" onClick={fetchLogs}>
                    Apply Filter
                  </Button>
                  {(logsDateFrom || logsDateTo) && (
                    <Button 
                      size="sm" 
                      variant="ghost"
                      onClick={() => {
                        setLogsDateFrom('');
                        setLogsDateTo('');
                      }}
                    >
                      Clear
                    </Button>
                  )}
                </div>

                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {logsLoading ? (
                    <p className="text-center text-gray-500 py-4">Loading...</p>
                  ) : logs.length === 0 ? (
                    <p className="text-center text-gray-500 py-4">No logs found. Click "Load Logs" to fetch.</p>
                  ) : (
                    logs.map((log) => (
                      <div 
                        key={log.id} 
                        className="p-3 bg-gray-50 rounded-lg border text-sm"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Badge variant="outline">{getActionLabel(log.action_type)}</Badge>
                            <span className="text-gray-600">by {log.admin_username}</span>
                          </div>
                          <span className="text-gray-400 text-xs">{formatDate(log.created_at)}</span>
                        </div>
                        <div className="mt-1 text-gray-600">
                          Target: <span className="font-medium">{log.target_identifier}</span>
                          {log.reason && <span className="text-gray-500"> - "{log.reason}"</span>}
                        </div>
                        {log.metadata && log.metadata.games_cancelled && (
                          <div className="text-gray-500 text-xs mt-1">
                            {log.metadata.games_cancelled} games cancelled
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Confirmation Dialog */}
      <AlertDialog open={confirmDialog.open} onOpenChange={(open) => !open && setConfirmDialog({ ...confirmDialog, open: false })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmDialog.action === 'disable_user' && 'Disable User'}
              {confirmDialog.action === 'enable_user' && 'Enable User'}
              {confirmDialog.action === 'cancel_all_games' && 'Cancel All Future Games'}
              {confirmDialog.action === 'cancel_game' && 'Cancel Game'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDialog.action === 'disable_user' && 
                `Are you sure you want to disable "${confirmDialog.target?.name}"? They will not be able to log in, create runs, or join runs.`}
              {confirmDialog.action === 'enable_user' && 
                `Are you sure you want to re-enable "${confirmDialog.target?.name}"?`}
              {confirmDialog.action === 'cancel_all_games' && 
                `Are you sure you want to cancel ALL future games for "${confirmDialog.target?.name}"? This cannot be undone.`}
              {confirmDialog.action === 'cancel_game' && 
                `Are you sure you want to cancel the game "${confirmDialog.target?.name}"?`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-4">
            <Label htmlFor="reason">Reason (optional)</Label>
            <Input
              id="reason"
              placeholder="Enter reason for this action..."
              value={confirmDialog.reason}
              onChange={(e) => setConfirmDialog({ ...confirmDialog, reason: e.target.value })}
              className="mt-2"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={executeAction}
              className={confirmDialog.action === 'enable_user' ? 'bg-orange-500 hover:bg-orange-600' : 'bg-red-500 hover:bg-red-600'}
            >
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminPanel;
